import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SignaturePad } from "@/components/SignaturePad";
import { FileSignature, Plus, Loader2, Calculator, Trash2, ArrowLeft, Save, ChevronRight, Send, Copy, PenTool, Download, CheckCircle2, RotateCcw, Clock, Upload, FileText, FileDown, Link2 } from "lucide-react";
import { fmtLogDate, orgToday } from "@/lib/tz";
import { useAutoSave } from "@/hooks/useAutoSave";
import { SaveIndicator } from "@/components/SaveIndicator";
import { TimeSelect } from "@/components/shift/shiftUi";
import { sanitizeDocHtml } from "@/components/RichTextEditor";

const TEAL = "#2f6f6b";
const FREQ = ["Daily", "Weekly", "Fortnightly", "Monthly", "Ad Hoc"];
const RATIOS = ["1:1", "1:2", "1:3", "1:4", "2:1"];
const PH = ["Charge at PH Rate", "Cancel Shift", "Move to Alternative Day"];
const NF2F_FREQ = ["Weekly", "Monthly", "Quarterly"];
const PRICED_FIELDS = ["total_budget", "total_instances", "sleepover_line", "idx", "transport_km_total", "transport_budget"];
const DAYS = [["monday", "Mon"], ["tuesday", "Tue"], ["wednesday", "Wed"], ["thursday", "Thu"], ["friday", "Fri"], ["saturday", "Sat"], ["sunday", "Sun"]];
const STATE_TONE = { Draft: "bg-amber-50 text-amber-700 border-amber-200", "Awaiting Signature": "bg-blue-50 text-blue-700 border-blue-200", Active: "bg-emerald-50 text-emerald-700 border-emerald-200", Scheduled: "bg-violet-50 text-violet-700 border-violet-200", Archived: "bg-slate-100 text-slate-500 border-slate-200" };
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const _t_hours_str = (t) => { const [h, m] = (t || "00:00").split(":"); return parseInt(h) + parseInt(m) / 60; };
// Parse in local time so a date never renders a day early/late in AU browsers.
const fmtDate = (s) => (s ? dayjs(s).format("YYYY-MM-DD") : "—");

export function ServiceAgreementsTab({ participant, participantName, canEdit, canDelete }) {
  const [list, setList] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [chooser, setChooser] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = () => api.serviceAgreements(participant).then(setList).catch(() => setList([]));
  useEffect(() => { load(); }, [participant]);

  if (openId || creating) {
    return <SAEditor participant={participant} participantName={participantName} saId={openId} canEdit={canEdit} canDelete={canDelete}
      onBack={() => { setOpenId(null); setCreating(false); load(); }} />;
  }

  return (
    <div data-testid="sa-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><FileSignature className="h-3.5 w-3.5" style={{ color: TEAL }} />Service Agreements</div>
        {canEdit && <Button size="sm" style={{ backgroundColor: TEAL }} onClick={() => setChooser(true)} data-testid="sa-new"><Plus className="h-4 w-4 mr-1.5" />New agreement</Button>}
      </div>
      {list === null ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : list.length === 0 ? (
        <div className="text-center py-14 text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">No service agreements yet. {canEdit && "Click \u201cNew agreement\u201d to create or upload one."}</div>
      ) : (
        <div className="space-y-2" data-testid="sa-list">
          {list.map((s) => (
            <button key={s.id} onClick={() => setOpenId(s.id)} data-testid={`sa-row-${s.id}`}
              className="w-full text-left rounded-lg border border-slate-200 bg-white p-3 hover:border-[#2f6f6b] hover:shadow-sm transition-all flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 flex items-center gap-2">{fmtDate(s.start_date)} → {fmtDate(s.end_date)}
                  {s.uploaded && <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5"><Upload className="h-2.5 w-2.5" />{s.source_system || "Uploaded"}</span>}
                </div>
                <div className="text-[11px] text-slate-400">{money(s.total_value)} · updated {fmtLogDate(s.updated_at)}</div>
              </div>
              <Badge className={`border ${STATE_TONE[s.workflow_state] || STATE_TONE.Draft}`}>{s.workflow_state}</Badge>
              <ChevronRight className="h-4 w-4 text-slate-300" />
            </button>
          ))}
        </div>
      )}

      <Dialog open={chooser} onOpenChange={setChooser}>
        <DialogContent data-testid="sa-chooser-dialog">
          <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><FileSignature className="h-4 w-4" style={{ color: TEAL }} />New service agreement</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-1">Build a fresh agreement with the pricing engine, or attach an already-signed one from a previous system.</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            <button onClick={() => { setChooser(false); setCreating(true); }} data-testid="sa-choose-build"
              className="rounded-lg border border-slate-200 p-4 text-left hover:border-[#2f6f6b] hover:shadow-sm transition-all">
              <Calculator className="h-5 w-5 mb-2" style={{ color: TEAL }} />
              <div className="text-sm font-semibold text-slate-800">Build a new agreement</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Price supports and send for e-signing.</div>
            </button>
            <button onClick={() => { setChooser(false); setUploadOpen(true); }} data-testid="sa-choose-upload"
              className="rounded-lg border border-slate-200 p-4 text-left hover:border-[#2f6f6b] hover:shadow-sm transition-all">
              <Upload className="h-5 w-5 mb-2" style={{ color: TEAL }} />
              <div className="text-sm font-semibold text-slate-800">Upload existing agreement</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Attach a signed copy from ShiftCare, FlowLogic, etc.</div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <SAUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} participant={participant} onDone={load} />
    </div>
  );
}

function SAEditor({ participant, participantName, saId, canEdit, canDelete, onBack }) {
  const [sa, setSa] = useState(saId ? null : { participant, supports: [], include_establishment_fee: false, has_representative: false });
  const [ref, setRef] = useState({ support_categories: [], ndis_items: [], holiday_lists: [] });
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [calcRes, setCalcRes] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const readOnly = sa && sa.workflow_state && sa.workflow_state !== "Draft";
  const editable = canEdit && !readOnly;

  const reload = () => { if (sa?.id) api.serviceAgreement(sa.id).then((d) => { setSa(d); setCalcRes(d.total_value ? d : null); }); };

  useEffect(() => {
    api.serviceAgreementsRef().then(setRef).catch(() => {});
    api.saTemplates("Published").then(setTemplates).catch(() => {});
    if (saId) api.serviceAgreement(saId).then((d) => { setSa(d); setCalcRes(d.total_value ? d : null); }).catch(() => toast.error("Could not load agreement."));
    else api.serviceAgreementParticipant(participant).then((p) => setSa((s) => ({ ...p, ...s }))).catch(() => {});
  }, [saId]);

  const { queueAuto, flushAll, globalSave, hasPending } = useAutoSave();

  const set = (k, v) => setSa((s) => {
    const next = { ...s, [k]: v };
    // Auto-save only existing drafts (not brand-new unsaved agreements).
    if (next.id && editable) queueAuto("sa-fields", () => api.serviceAgreementUpdate(next.id, bodyFrom(next)));
    return next;
  });
  const setLine = (i, k, v) => setSa((s) => {
    const sup = [...(s.supports || [])]; sup[i] = { ...sup[i], [k]: v };
    const next = { ...s, supports: sup };
    if (next.id && editable) queueAuto("sa-fields", () => api.serviceAgreementUpdate(next.id, bodyFrom(next)));
    return next;
  });
  const addLine = () => setSa((s) => {
    const next = { ...s, supports: [...(s.supports || []), { frequency: "Weekly", staff_ratio: "1:1", public_holiday_treatment: "Charge at PH Rate" }] };
    if (next.id && editable) queueAuto("sa-fields", () => api.serviceAgreementUpdate(next.id, bodyFrom(next)));
    return next;
  });
  // Copy a line (e.g. to change days part way through the agreement). Priced
  // results are dropped so the copy shows nothing until it's recalculated.
  const dupLine = (i) => setSa((s) => {
    const copy = Object.fromEntries(Object.entries(s.supports[i]).filter(([k]) => !PRICED_FIELDS.includes(k)));
    const sup = [...s.supports]; sup.splice(i + 1, 0, copy);
    const next = { ...s, supports: sup };
    if (next.id && editable) queueAuto("sa-fields", () => api.serviceAgreementUpdate(next.id, bodyFrom(next)));
    return next;
  });
  const delLine = (i) => setSa((s) => {
    const next = { ...s, supports: s.supports.filter((_, x) => x !== i) };
    if (next.id && editable) queueAuto("sa-fields", () => api.serviceAgreementUpdate(next.id, bodyFrom(next)));
    return next;
  });

  // bodyFrom accepts a snapshot `s` so auto-save closures capture the latest
  // state rather than the stale `sa` from the closure at queue time.
  const bodyFrom = (s) => ({
    participant, service_agreement_template: s.service_agreement_template || null,
    start_date: s.start_date || null, end_date: s.end_date || null, holiday_list: s.holiday_list || null,
    include_establishment_fee: !!s.include_establishment_fee, has_representative: !!s.has_representative,
    representative_full_name: s.representative_full_name || null, representative_relationship: s.representative_relationship || null,
    representative_phone: s.representative_phone || null, representative_email: s.representative_email || null,
    supports: s.supports || [],
  });
  const body = () => bodyFrom(sa);

  const ensureSaved = async () => {
    // Flush any pending auto-save before an explicit save / calculate so we
    // don't create a duplicate write.
    flushAll();
    if (sa.id) { await api.serviceAgreementUpdate(sa.id, body()); return sa.id; }
    const created = await api.serviceAgreementCreate(body());
    setSa(created); return created.id;
  };
  const save = async () => {
    setBusy(true);
    try { await ensureSaved(); toast.success("Draft saved."); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };
  const calculate = async () => {
    if (!sa.start_date || !sa.end_date) { toast.error("Set start and end dates first."); return; }
    setBusy(true);
    try { const id = await ensureSaved(); const res = await api.serviceAgreementCalculate(id); setCalcRes(res); setSa((s) => ({ ...s, ...res })); toast.success(`Priced: ${money(res.total_value)}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Calculation failed."); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!sa.id || !window.confirm("Delete this draft agreement?")) return;
    try { await api.serviceAgreementDelete(sa.id); toast.success("Deleted."); onBack(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed."); }
  };

  if (!sa) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  if (sa.uploaded) return <UploadedSAView sa={sa} canDelete={canDelete} onBack={onBack} />;

  const locked = ["Active", "Scheduled", "Archived"].includes(sa.workflow_state);

  const dlFinalPdf = async () => {
    try {
      const blob = await api.saFinalPdf(sa.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SA_Signed_${(sa.participant_name || "Participant").replace(/\s+/g, "_")}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't download the PDF."); }
  };

  // When the agreement is fully signed (Active/Archived), hide all content and
  // direct users to open the PDF generated at signing — same behaviour as Care Plans.
  if (locked) {
    const sg = sa.signing || {};
    const isActive = sa.workflow_state === "Active" || sa.workflow_state === "Scheduled";
    return (
      <div data-testid="sa-editor">
        <div className="flex items-center justify-between mb-4">
          <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800" data-testid="sa-back">
            <ArrowLeft className="h-3.5 w-3.5" />Back to agreements
          </button>
          <Badge className={`border ${STATE_TONE[sa.workflow_state] || STATE_TONE.Draft}`}>{sa.workflow_state}</Badge>
        </div>

        {/* Locked banner */}
        <div className={`rounded-lg border-2 p-5 mb-4 ${isActive ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-slate-50"}`} data-testid="sa-locked-banner">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className={`h-5 w-5 ${isActive ? "text-emerald-600" : "text-slate-400"}`} />
            <span className={`text-sm font-semibold ${isActive ? "text-emerald-800" : "text-slate-600"}`}>
              {sa.workflow_state === "Scheduled" ? `Agreement signed — starts ${fmtDate(sa.start_date)}` : isActive ? "Agreement signed and active" : "Archived agreement"}
            </span>
          </div>
          <p className="text-xs text-slate-600 mb-3">
            This service agreement is fully signed and locked. The details of this agreement are stored in the PDF generated at the time of signing — open that PDF to view the full agreement terms, schedule of supports and financial summary.
          </p>
          {sg.participant_signed_on && (
            <p className="text-xs text-slate-500 mb-1">
              Signed by <span className="font-medium">{sg.participant_signature_text || sg.sent_to_name || "participant"}</span> on {fmtDate(sg.participant_signed_on)}.
              {sg.office_signed_on && <> Counter-signed by <span className="font-medium">{sg.office_signed_by}</span> on {fmtDate(sg.office_signed_on)}.</>}
            </p>
          )}
          {sa.agreement_terms && (
            <Button size="sm" style={{ backgroundColor: TEAL }} onClick={dlFinalPdf} className="mt-2" data-testid="sa-download-final-pdf">
              <Download className="h-4 w-4 mr-1.5" />Download signed agreement PDF
            </Button>
          )}
          {!sa.agreement_terms && (
            <p className="text-xs text-slate-400 italic mt-1">PDF not available — this agreement was signed before PDF generation was introduced.</p>
          )}
          {sg.signed_copy_url && (
            <Button size="sm" variant="outline" onClick={() => openSignedCopy(sa.id)} className="mt-2 ml-2" data-testid="sa-locked-signed-copy">
              <FileText className="h-4 w-4 mr-1.5" />Signed copy (uploaded)
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="sa-editor">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => {
          if (hasPending && !window.confirm("You have unsaved changes — leave without saving?")) return;
          onBack();
        }} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800" data-testid="sa-back"><ArrowLeft className="h-3.5 w-3.5" />Back to agreements</button>
        {sa.workflow_state && <Badge className={`border ${STATE_TONE[sa.workflow_state] || STATE_TONE.Draft}`}>{sa.workflow_state}</Badge>}
      </div>

      {readOnly && <div className="mb-4 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2.5">This agreement is {sa.workflow_state} and is read-only.</div>}

      {/* Participant (auto-filled, locked) */}
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 mb-4">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Participant (from profile)</div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-700">
          <div><span className="text-slate-400">Name:</span> {sa.participant_name || participantName}</div>
          <div><span className="text-slate-400">NDIS:</span> {sa.ndis_number || "—"}</div>
          <div><span className="text-slate-400">Phone:</span> {sa.participant_phone || "—"}</div>
          <div><span className="text-slate-400">Email:</span> {sa.participant_email || "—"}</div>
        </div>
      </div>

      {/* Core */}
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <Fld label="Template (T&Cs)">
          <Select value={sa.service_agreement_template || ""} onValueChange={(v) => set("service_agreement_template", v)} disabled={!editable}>
            <SelectTrigger className="h-9 text-sm bg-white" data-testid="sa-template"><SelectValue placeholder="Select a published template" /></SelectTrigger>
            <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id} className="text-sm">{t.template_name}</SelectItem>)}</SelectContent>
          </Select>
        </Fld>
        <Fld label="Holiday list (for PH rates)">
          <Select value={sa.holiday_list || ""} onValueChange={(v) => set("holiday_list", v)} disabled={!editable}>
            <SelectTrigger className="h-9 text-sm bg-white" data-testid="sa-holiday"><SelectValue placeholder="Optional" /></SelectTrigger>
            <SelectContent>{ref.holiday_lists.map((h) => <SelectItem key={h} value={h} className="text-sm">{h}</SelectItem>)}</SelectContent>
          </Select>
        </Fld>
        <Fld label="Start date"><Input type="date" value={sa.start_date || ""} onChange={(e) => set("start_date", e.target.value)} disabled={!editable} className="h-9 text-sm" data-testid="sa-start" /></Fld>
        <Fld label="End date"><Input type="date" value={sa.end_date || ""} onChange={(e) => set("end_date", e.target.value)} disabled={!editable} className="h-9 text-sm" data-testid="sa-end" /></Fld>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700 mb-4">
        <Checkbox checked={!!sa.include_establishment_fee} onCheckedChange={(v) => set("include_establishment_fee", !!v)} disabled={!editable} data-testid="sa-estfee" />
        Include establishment fee (≥20 hrs/month of 01/04 supports, or SIL Weekly — one-off after 4 weeks)
      </label>

      {/* Representative (who signs on the participant's behalf) */}
      <div className="rounded-lg border border-slate-200 bg-white p-3 mb-4">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Checkbox checked={!!sa.has_representative} onCheckedChange={(v) => set("has_representative", !!v)} disabled={!editable} data-testid="sa-has-rep" />
          Participant has a representative who signs on their behalf
        </label>
        {sa.has_representative && (
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <Fld label="Representative name"><Input value={sa.representative_full_name || ""} onChange={(e) => set("representative_full_name", e.target.value)} disabled={!editable} className="h-9 text-sm" data-testid="sa-rep-name" /></Fld>
            <Fld label="Relationship"><Input value={sa.representative_relationship || ""} onChange={(e) => set("representative_relationship", e.target.value)} disabled={!editable} className="h-9 text-sm" data-testid="sa-rep-rel" /></Fld>
            <Fld label="Representative email"><Input value={sa.representative_email || ""} onChange={(e) => set("representative_email", e.target.value)} disabled={!editable} className="h-9 text-sm" data-testid="sa-rep-email" /></Fld>
            <Fld label="Representative phone"><Input value={sa.representative_phone || ""} onChange={(e) => set("representative_phone", e.target.value)} disabled={!editable} className="h-9 text-sm" data-testid="sa-rep-phone" /></Fld>
          </div>
        )}
      </div>

      {/* Supports */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Supports</div>
        {editable && <Button size="sm" variant="outline" onClick={addLine} data-testid="sa-add-line"><Plus className="h-3.5 w-3.5 mr-1" />Add support</Button>}
      </div>
      <div className="space-y-2 mb-4" data-testid="sa-supports">
        {(sa.supports || []).length === 0 && <div className="text-xs text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-lg">No supports added.</div>}
        {(sa.supports || []).map((r, i) => (
          <SupportLine key={i} r={r} i={i} cats={ref.support_categories} ndisItems={ref.ndis_items || []} saStart={sa.start_date} saEnd={sa.end_date} editable={editable} onChange={setLine} onDuplicate={() => dupLine(i)} onDelete={() => delLine(i)} />
        ))}
      </div>

      {editable && (
        <div className="flex items-center gap-2 mb-4">
          <Button size="sm" onClick={calculate} disabled={busy} style={{ backgroundColor: TEAL }} data-testid="sa-calculate">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Calculator className="h-4 w-4 mr-1.5" />}Calculate</Button>
          <Button size="sm" variant="outline" onClick={save} disabled={busy} data-testid="sa-save"><Save className="h-4 w-4 mr-1.5" />Save draft</Button>
          <SaveIndicator st={globalSave} className="ml-1" />
          {sa.id && <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 ml-auto" onClick={remove} data-testid="sa-delete"><Trash2 className="h-4 w-4 mr-1.5" />Delete</Button>}
        </div>
      )}

      {(calcRes || sa.total_value > 0) && (
        <div className="space-y-4" data-testid="sa-results">
          <div className="rounded-lg border-2 border-teal-200 bg-teal-50/50 p-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-teal-800">Total agreement value</span>
            <span className="text-2xl font-bold text-teal-900 font-brand">{money(sa.total_value)}</span>
          </div>
          {sa.sos_preview_data && <div className="rounded-lg border border-slate-200 bg-white p-2 overflow-x-auto" data-testid="sa-sos" dangerouslySetInnerHTML={{ __html: sanitizeDocHtml(sa.sos_preview_data) }} />}
          {sa.financial_summary_data && <div className="rounded-lg border border-slate-200 bg-white p-2 overflow-x-auto" data-testid="sa-fs" dangerouslySetInnerHTML={{ __html: sanitizeDocHtml(sa.financial_summary_data) }} />}
          {sa.agreement_terms && <div className="rounded-lg border border-slate-200 bg-white p-4 prose-sm max-w-none" data-testid="sa-terms" dangerouslySetInnerHTML={{ __html: sanitizeDocHtml(sa.agreement_terms) }} />}
        </div>
      )}

      {sa.id && (
        <SASigningPanel sa={sa} canEdit={canEdit}
          onSend={() => setSendOpen(true)} onSign={() => setSignOpen(true)} onReload={reload} />
      )}

      {sa.id && (
        <SASendDialog open={sendOpen} onOpenChange={setSendOpen} sa={sa}
          isResend={sa.workflow_state === "Awaiting Signature"} onDone={reload} />
      )}
      {sa.id && (
        <SAOfficeSignDialog open={signOpen} onOpenChange={setSignOpen} saId={sa.id} onSigned={reload} />
      )}
    </div>
  );
}

function Fld({ label, children }) {
  return <div className="space-y-1"><Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</Label>{children}</div>;
}

function AdHocFields({ r, i, editable, onChange, filteredItems, standardRate }) {
  return (
    <div className="space-y-2">
      {/* Row 1: Item dropdown + Quantity */}
      <div className="grid sm:grid-cols-3 gap-2">
        <div className="sm:col-span-2">
          <Select
            value={r.ad_hoc_item || ""}
            onValueChange={(v) => {
              onChange(i, "ad_hoc_item", v);
              // Clear manual override amount when item changes (so it re-reads from price book)
              if (!r.use_manual_price_override) {
                onChange(i, "manual_price_override", 0);
              }
            }}
            disabled={!editable}
          >
            <SelectTrigger className="h-9 text-sm" data-testid={`sa-line-adhoc-item-${i}`}>
              <SelectValue placeholder="Select ad hoc item code…" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {filteredItems.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-400">No items found — select a support category first.</div>
              ) : (
                filteredItems.map((it) => (
                  <SelectItem key={it.code} value={it.code} className="text-sm">
                    <span className="font-mono text-xs text-slate-500 mr-1.5">{it.code}</span>{it.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
        <Input
          type="number"
          placeholder="Quantity"
          value={r.ad_hoc_quantity || ""}
          onChange={(e) => onChange(i, "ad_hoc_quantity", parseFloat(e.target.value) || 0)}
          disabled={!editable}
          className="h-9 text-sm"
          data-testid={`sa-line-adhoc-qty-${i}`}
        />
      </div>

      {/* Row 2: Manual price override toggle + price field */}
      <div className="space-y-1.5">
        <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
          <Checkbox
            checked={!!r.use_manual_price_override}
            onCheckedChange={(v) => {
              onChange(i, "use_manual_price_override", !!v);
              // Pre-fill with the standard rate when the override is first enabled
              if (v && standardRate !== null && !r.manual_price_override) {
                onChange(i, "manual_price_override", standardRate);
              }
            }}
            disabled={!editable}
            data-testid={`sa-line-adhoc-override-chk-${i}`}
          />
          Manual price override
        </label>
        {r.use_manual_price_override ? (
          <Input
            type="number"
            step="0.01"
            placeholder="Override charge amount"
            value={r.manual_price_override || ""}
            onChange={(e) => onChange(i, "manual_price_override", parseFloat(e.target.value) || 0)}
            disabled={!editable}
            className="h-9 text-sm"
            data-testid={`sa-line-adhoc-price-${i}`}
          />
        ) : (
          standardRate !== null && (
            <p className="text-[11px] text-slate-500 pl-1">
              Standard price (price book): <span className="font-semibold text-slate-700">{money(standardRate)}</span> per unit
            </p>
          )
        )}
      </div>

      {/* Row 3: Comment / notes — shown on Schedule of Supports */}
      <textarea
        rows={2}
        placeholder="Notes / description — what this is for (shown on Schedule of Supports)"
        value={r.ad_hoc_comment || ""}
        onChange={(e) => onChange(i, "ad_hoc_comment", e.target.value)}
        disabled={!editable}
        className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-[#2f6f6b] resize-none disabled:opacity-60"
        data-testid={`sa-line-adhoc-comment-${i}`}
      />
    </div>
  );
}

function SupportLine({ r, i, cats, ndisItems, saStart, saEnd, editable, onChange, onDuplicate, onDelete }) {
  const freq = r.frequency || "Weekly";
  const isAdHoc = freq === "Ad Hoc";
  const showDays = freq === "Weekly" || freq === "Fortnightly";
  const catQ = useMemo(() => cats, [cats]);

  // Filter NDIS items by selected support category when one is chosen
  const filteredItems = useMemo(() => {
    if (!ndisItems || ndisItems.length === 0) return [];
    if (r.support_category) {
      const filtered = ndisItems.filter((it) => it.group === r.support_category);
      return filtered.length > 0 ? filtered : ndisItems;
    }
    return ndisItems;
  }, [ndisItems, r.support_category]);

  // Lookup the standard rate for the currently selected ad hoc item
  const standardRate = useMemo(() => {
    if (!r.ad_hoc_item || !ndisItems) return null;
    const found = ndisItems.find((it) => it.code === r.ad_hoc_item);
    return found ? found.rate : null;
  }, [r.ad_hoc_item, ndisItems]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`sa-line-${i}`}>
      <div className="grid sm:grid-cols-3 gap-2 mb-2">
        <div className="sm:col-span-2">
          <Select value={r.support_category || ""} onValueChange={(v) => onChange(i, "support_category", v)} disabled={!editable}>
            <SelectTrigger className="h-9 text-sm" data-testid={`sa-line-cat-${i}`}><SelectValue placeholder="Support category" /></SelectTrigger>
            <SelectContent className="max-h-72">{catQ.map((c) => <SelectItem key={c} value={c} className="text-sm">{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Select value={freq} onValueChange={(v) => onChange(i, "frequency", v)} disabled={!editable}>
          <SelectTrigger className="h-9 text-sm" data-testid={`sa-line-freq-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent>{FREQ.map((f) => <SelectItem key={f} value={f} className="text-sm">{f}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {isAdHoc ? (
        <AdHocFields r={r} i={i} editable={editable} onChange={onChange} filteredItems={filteredItems} standardRate={standardRate} />
      ) : (
        <>
          <div className="grid sm:grid-cols-4 gap-2 mb-2">
            <TimeSelect value={(r.start_time || "").slice(0, 5)} onChange={(v) => onChange(i, "start_time", v + ":00")} disabled={!editable} data-testid={`sa-line-start-${i}`} />
            <TimeSelect value={(r.end_time || "").slice(0, 5)} onChange={(v) => onChange(i, "end_time", v + ":00")} disabled={!editable} data-testid={`sa-line-end-${i}`} />
            <Select value={r.staff_ratio || "1:1"} onValueChange={(v) => onChange(i, "staff_ratio", v)} disabled={!editable}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{RATIOS.map((x) => <SelectItem key={x} value={x} className="text-sm">Ratio {x}</SelectItem>)}</SelectContent>
            </Select>
            {freq === "Fortnightly" && <Input type="number" placeholder="Repeat every (wks)" value={r.repeat_every || ""} onChange={(e) => onChange(i, "repeat_every", parseInt(e.target.value) || 1)} disabled={!editable} className="h-9 text-sm" />}
            {freq === "Monthly" && <Input type="number" placeholder="Date of month" value={r.date_of_month || ""} onChange={(e) => onChange(i, "date_of_month", parseInt(e.target.value) || 1)} disabled={!editable} className="h-9 text-sm" />}
          </div>
          {showDays && (
            <div className="flex flex-wrap gap-3 mb-2">
              {DAYS.map(([k, lbl]) => (
                <label key={k} className="flex items-center gap-1.5 text-xs text-slate-600">
                  <Checkbox checked={!!r[k]} onCheckedChange={(v) => onChange(i, k, !!v)} disabled={!editable} data-testid={`sa-line-${k}-${i}`} />{lbl}
                </label>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-[11px] text-slate-500">From</span>
            <Input type="date" value={(r.line_start_date || "").slice(0, 10)} min={saStart || undefined} max={saEnd || undefined} onChange={(e) => onChange(i, "line_start_date", e.target.value || null)} disabled={!editable} className="h-8 text-xs w-40" data-testid={`sa-line-from-${i}`} />
            <span className="text-[11px] text-slate-500">to</span>
            <Input type="date" value={(r.line_end_date || "").slice(0, 10)} min={saStart || undefined} max={saEnd || undefined} onChange={(e) => onChange(i, "line_end_date", e.target.value || null)} disabled={!editable} className="h-8 text-xs w-40" data-testid={`sa-line-to-${i}`} />
            <span className="text-[11px] text-slate-400">Leave blank to use the agreement dates</span>
          </div>
        </>
      )}

      <div className="grid sm:grid-cols-3 gap-2 mt-2">
        <Input type="number" placeholder="Transport (km)" value={r.transport_km || ""} onChange={(e) => onChange(i, "transport_km", parseFloat(e.target.value) || 0)} disabled={!editable} className="h-9 text-sm" />
        <Input type="number" placeholder="Provider travel (mins)" value={r.provider_travel_mins || ""} onChange={(e) => onChange(i, "provider_travel_mins", parseInt(e.target.value) || 0)} disabled={!editable} className="h-9 text-sm" />
        <Select value={r.public_holiday_treatment || "Charge at PH Rate"} onValueChange={(v) => onChange(i, "public_holiday_treatment", v)} disabled={!editable}>
          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>{PH.map((x) => <SelectItem key={x} value={x} className="text-sm">{x}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between mt-2">
        <div className="flex flex-col gap-1.5">
          <label className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <Checkbox checked={!!r.is_separate_shift} onCheckedChange={(v) => onChange(i, "is_separate_shift", !!v)} disabled={!editable} />Separate shift (do not merge)
          </label>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-500" data-testid={`sa-line-sleepover-label-${i}`}>
            <Checkbox checked={!!r.is_inactive_sleepover} onCheckedChange={(v) => onChange(i, "is_inactive_sleepover", !!v)} disabled={!editable} data-testid={`sa-line-sleepover-${i}`} />
            <span className="flex items-center gap-1">
              Inactive Sleepover
              <span className="inline-flex items-center rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200 ml-1">auto line item</span>
            </span>
          </label>
          {!!r.is_inactive_sleepover && (() => {
            const startH = _t_hours_str(r.sleepover_start || "22:00");
            const endH = _t_hours_str(r.sleepover_end || "06:00");
            const inactiveHrs = endH <= startH ? (24 - startH + endH) : (endH - startH);
            return (
              <div className="flex flex-col gap-1 pl-5 mt-0.5">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-400 w-20">Inactive from</span>
                  <TimeSelect
                    value={r.sleepover_start || "22:00"}
                    onChange={(v) => onChange(i, "sleepover_start", v)}
                    disabled={!editable}
                    className="w-32"
                    data-testid={`sa-line-sleepover-start-${i}`}
                  />
                  <span className="text-[11px] text-slate-400">to</span>
                  <TimeSelect
                    value={r.sleepover_end || "06:00"}
                    onChange={(v) => onChange(i, "sleepover_end", v)}
                    disabled={!editable}
                    className="w-32"
                    data-testid={`sa-line-sleepover-end-${i}`}
                  />
                  <span className="text-[11px] text-slate-400">({inactiveHrs.toFixed(1)} hrs inactive per night)</span>
                </div>
              </div>
            );
          })()}
          <label className="flex items-center gap-1.5 text-[11px] text-slate-500" data-testid={`sa-line-nf2f-label-${i}`}>
            <Checkbox checked={!!r.is_non_face_to_face} onCheckedChange={(v) => onChange(i, "is_non_face_to_face", !!v)} disabled={!editable} data-testid={`sa-line-nf2f-${i}`} />
            <span className="flex items-center gap-1">
              Non-face-to-face support
              <span className="inline-flex items-center rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-blue-50 text-blue-700 border border-blue-200 ml-1">NF2F</span>
            </span>
          </label>
          {!!r.is_non_face_to_face && (
            <div className="flex items-center gap-2 pl-5 mt-0.5">
              <Select value={r.nf2f_frequency || "Weekly"} onValueChange={(v) => onChange(i, "nf2f_frequency", v)} disabled={!editable}>
                <SelectTrigger className="h-8 text-xs w-40" data-testid={`sa-line-nf2f-freq-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{NF2F_FREQ.map((f) => <SelectItem key={f} value={f} className="text-xs">{f}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-[11px] text-slate-400">billing frequency</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {r.total_budget > 0 && <span className="text-xs font-semibold text-teal-700">{r.total_instances} × = {money(r.total_budget)}</span>}
          {editable && <button onClick={onDuplicate} title="Duplicate support" className="text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-100" data-testid={`sa-line-dup-${i}`}><Copy className="h-3.5 w-3.5" /></button>}
          {editable && <button onClick={onDelete} className="text-slate-400 hover:text-rose-600 p-1 rounded hover:bg-rose-50" data-testid={`sa-line-del-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>}
        </div>
      </div>
    </div>
  );
}


async function openSignedCopy(id) {
  try {
    const blob = await api.saSignedCopy(id);
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error("Couldn't open the signed copy."); }
}

function SASigningPanel({ sa, canEdit, onSend, onSign, onReload }) {
  const state = sa.workflow_state || "Draft";
  const sg = sa.signing || {};
  const signed = !!sg.participant_signed_on;
  const signerLabel = sg.signer_role === "representative" ? "representative" : "participant";
  const [downloadingDraft, setDownloadingDraft] = useState(false);
  const [uploading, setUploading] = useState(false);

  const revert = async () => {
    if (!window.confirm("Revert this agreement to draft? Any collected signature link will be invalidated.")) return;
    try { await api.saRevert(sa.id); toast.success("Reverted to draft."); onReload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };

  const downloadDraft = async () => {
    setDownloadingDraft(true);
    try {
      const blob = await api.saDraftPdf(sa.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeName = (sa.participant_name || "Participant").replace(/\s+/g, "_");
      const dateStamp = orgToday().replace(/-/g, "");
      a.href = url;
      a.download = `SA_DRAFT_${safeName}_${dateStamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success("Draft PDF downloaded — not for signing.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate draft PDF.");
    } finally {
      setDownloadingDraft(false);
    }
  };

  if (state === "Draft") {
    if (!canEdit) return null;
    const ready = !!sa.agreement_terms;
    return (
      <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid="sa-signing-draft">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Signing actions</div>

        {!ready && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            Choose a template and click <strong>Calculate</strong> to prepare the agreement content before you can download a draft or send for signing.
          </p>
        )}

        {/* ── Action 1: Internal review download ── */}
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-1.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
            📋 Internal review
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="text-xs text-amber-800 leading-snug">
                Download a <strong>draft PDF</strong> to review agreement content internally.
                <br />
                <span className="font-semibold">This file is never sent to the participant</span> — it is
                stamped <em>"DRAFT — NOT FOR SIGNING"</em> and contains no signature blocks.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 border-amber-400 text-amber-800 hover:bg-amber-100 hover:border-amber-500 disabled:opacity-50"
              disabled={!ready || downloadingDraft}
              onClick={downloadDraft}
              data-testid="sa-download-draft"
            >
              {downloadingDraft
                ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                : <FileDown className="h-4 w-4 mr-1.5" />}
              Download draft PDF
            </Button>
          </div>
        </div>

        {/* ── Divider ── */}
        <div className="flex items-center gap-2">
          <div className="flex-1 border-t border-slate-200" />
          <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">or</span>
          <div className="flex-1 border-t border-slate-200" />
        </div>

        {/* ── Action 2: Send to participant ── */}
        <div className="rounded-md border border-teal-200 bg-teal-50/60 p-3 space-y-1.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-teal-700">
            📤 Send to participant
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="text-xs text-teal-900 leading-snug">
                Send the agreement to the participant (or their representative) for <strong>e-signing</strong>
                via email, SMS, or a manually shared link.
                <br />
                <span className="font-semibold">This will contact the participant</span> — ensure the content is finalised first.
              </p>
            </div>
            <Button
              size="sm"
              style={{ backgroundColor: TEAL }}
              className="shrink-0 disabled:opacity-50"
              disabled={!ready}
              onClick={onSend}
              data-testid="sa-send-signing"
            >
              <Send className="h-4 w-4 mr-1.5" />Send for signing
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const uploadSigned = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") { toast.error("Please upload the signed copy as a PDF."); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("PDF is too large (max 15MB)."); return; }
    setUploading(true);
    try {
      const data = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      await api.saUploadSigned(sa.id, { filename: file.name, data });
      toast.success("Signed copy uploaded. The office can now counter-sign.");
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); }
  };

  const downloadSigningPdf = async () => {
    try {
      const blob = await api.saSigningPdf(sa.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SA_ForSigning_${(sa.participant_name || "Participant").replace(/\s+/g, "_")}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't download the signing PDF."); }
  };

  if (state === "Awaiting Signature") {
    const paperSigned = signed && !!sg.signed_copy_url;
    return (
      <div className="mt-5 rounded-lg border border-blue-200 bg-blue-50/60 p-4" data-testid="sa-signing-awaiting">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-semibold text-blue-800">Awaiting signature</span>
        </div>
        {paperSigned ? (
          <p className="text-xs text-slate-600 mb-3">Signed copy uploaded{sg.signed_copy_uploaded_at ? ` on ${fmtLogDate(sg.signed_copy_uploaded_at)}` : ""}{sg.participant_signature_text ? ` (signed by ${sg.participant_signature_text})` : ""}. Counter-sign to activate the agreement.</p>
        ) : signed ? (
          <p className="text-xs text-slate-600 mb-3">Signed by the {signerLabel} ({sg.participant_signature_text || sg.sent_to_name}) on {fmtDate(sg.participant_signed_on)}. Counter-sign to activate the agreement.</p>
        ) : (
          <p className="text-xs text-slate-600 mb-3">Sent to the {signerLabel}{sg.sent_to_name ? ` (${sg.sent_to_name})` : ""}{sg.sent_to_email ? ` · ${sg.sent_to_email}` : ""}{sg.sent_to_mobile ? ` · ${sg.sent_to_mobile}` : ""}. Waiting for them to sign. If they signed a printed or emailed copy instead, upload it here.</p>
        )}
        {!signed && sg.link && (
          <div className="flex gap-2 mb-3">
            <Input value={sg.link} readOnly className="h-8 text-xs" data-testid="sa-signing-link" />
            <Button size="sm" variant="outline" className="h-8" onClick={() => { navigator.clipboard.writeText(sg.link); toast.success("Copied."); }}><Copy className="h-3.5 w-3.5" /></Button>
          </div>
        )}
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            {signed && <Button size="sm" style={{ backgroundColor: TEAL }} onClick={onSign} data-testid="sa-office-sign"><PenTool className="h-4 w-4 mr-1.5" />Office counter-sign</Button>}
            {!signed && <Button size="sm" variant="outline" onClick={onSend} data-testid="sa-resend"><Send className="h-4 w-4 mr-1.5" />Resend</Button>}
            {!signed && <Button size="sm" variant="outline" onClick={downloadSigningPdf} data-testid="sa-download-signing"><Download className="h-4 w-4 mr-1.5" />Download signing PDF</Button>}
            {(!signed || paperSigned) && (
              <label className={`inline-flex items-center h-8 px-3 rounded-md border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="sa-upload-signed-label">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}{paperSigned ? "Replace signed copy" : "Upload signed copy"}
                <input type="file" accept="application/pdf" className="hidden"
                  onChange={(e) => { uploadSigned(e.target.files?.[0]); e.target.value = ""; }} data-testid="sa-upload-signed" />
              </label>
            )}
            {paperSigned && <Button size="sm" variant="outline" onClick={() => openSignedCopy(sa.id)} data-testid="sa-view-signed-copy"><FileText className="h-4 w-4 mr-1.5" />View signed copy</Button>}
            {!sg.office_signed_on && <Button size="sm" variant="ghost" className="text-slate-500" onClick={revert} data-testid="sa-revert"><RotateCcw className="h-4 w-4 mr-1.5" />Revert to draft</Button>}
          </div>
        )}
      </div>
    );
  }

  // Active / Archived
  return (
    <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50/60 p-4" data-testid="sa-signing-final">
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        <span className="text-sm font-semibold text-emerald-800">{state === "Active" ? "Active agreement" : state === "Scheduled" ? `Scheduled — starts ${fmtDate(sa.start_date)}` : "Archived"}</span>
      </div>
      <p className="text-xs text-slate-600 mt-1">
        {sg.participant_signed_on && <>Signed by {sg.participant_signature_text || sg.sent_to_name} on {fmtDate(sg.participant_signed_on)}. </>}
        {sg.office_signed_on && <>Counter-signed by {sg.office_signed_by} on {fmtDate(sg.office_signed_on)}.</>}
      </p>
    </div>
  );
}

function SASendDialog({ open, onOpenChange, sa, isResend, onDone }) {
  const [method, setMethod] = useState("email");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const rep = sa.has_representative && (sa.representative_full_name || sa.representative_email);
  const name = rep ? sa.representative_full_name : sa.participant_name;
  const [email, setEmail] = useState(rep ? sa.representative_email || "" : sa.participant_email || "");
  const [mobile, setMobile] = useState(rep ? sa.representative_phone || "" : sa.participant_phone || "");

  useEffect(() => { if (open) { setLink(""); setMethod("email"); } }, [open]);

  const dlBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const send = async () => {
    setBusy(true);
    try {
      const body = { method, to_name: name, to_email: email, to_mobile: mobile };
      const res = isResend ? await api.saResendSigning(sa.id, body) : await api.saSendForSigning(sa.id, body);
      if (method === "external") {
        setLink(res.link || "");
        toast.success("Marked for external signing. Share the link below.");
      } else if (method === "in_person") {
        toast.success("Marked for in-person / OPG signing. Downloading signing PDF…");
        try {
          const blob = await api.saSigningPdf(sa.id);
          dlBlob(blob, `SA_ForSigning_${(name || "Participant").replace(/\s+/g, "_")}.pdf`);
        } catch { toast.error("Couldn't download the signing PDF — try again from the agreement."); }
        onOpenChange(false);
      } else if (method === "portal") {
        toast.success("Agreement made available in the participant portal for signing.");
        onOpenChange(false);
      } else {
        toast.success(`Signing link sent by ${method}.`);
        onOpenChange(false);
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sa-send-dialog">
        <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><Send className="h-4 w-4" style={{ color: TEAL }} />{isResend ? "Resend" : "Send"} for signing</DialogTitle></DialogHeader>
        {link ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">Share this secure link with the {rep ? "representative" : "participant"}. They verify with the NDIS number & date of birth. Expires in 5 days.</p>
            <div className="flex gap-2">
              <Input value={link} readOnly className="h-9 text-xs" data-testid="sa-send-link" />
              <Button onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied."); }} variant="outline" className="h-9"><Copy className="h-4 w-4" /></Button>
            </div>
            <DialogFooter><Button onClick={() => onOpenChange(false)} style={{ backgroundColor: TEAL }}>Done</Button></DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5 text-[11px] text-slate-600">
              Signs as: <b>{rep ? "Representative" : "Participant"}</b>{name ? ` — ${name}` : ""}. Then the office counter-signs to activate.
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Signing method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="sa-send-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email" className="text-sm">Email a signing link</SelectItem>
                  <SelectItem value="sms" className="text-sm">SMS a signing link</SelectItem>
                  <SelectItem value="portal" className="text-sm">Participant portal (participant logs in to sign)</SelectItem>
                  <SelectItem value="in_person" className="text-sm">In person / OPG / Physical (download PDF to sign)</SelectItem>
                  <SelectItem value="external" className="text-sm">External (copy link to send manually)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {method === "email" && <div><Label className="text-[11px] font-semibold text-slate-600">Email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-9 text-sm" data-testid="sa-send-email" /></div>}
            {method === "sms" && <div><Label className="text-[11px] font-semibold text-slate-600">Mobile</Label><Input value={mobile} onChange={(e) => setMobile(e.target.value)} className="mt-1 h-9 text-sm" data-testid="sa-send-mobile" /></div>}
            {method === "portal" && <div className="rounded-md bg-teal-50 border border-teal-200 p-2.5 text-[11px] text-teal-800">No email or SMS is sent. The agreement becomes available to sign in the participant portal — the participant or any linked guardian / decision-maker with portal access can review and sign it when they log in.</div>}
            {method === "in_person" && <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5 text-[11px] text-slate-600">No email or SMS is sent. A PDF with blank participant / guardian and office signature sections downloads now — print it or send it to the OPG. Once it's signed and returned, upload the signed copy and the office can counter-sign.</div>}
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={send} disabled={busy} style={{ backgroundColor: TEAL }} data-testid="sa-send-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : method === "in_person" ? <Download className="h-4 w-4 mr-1.5" /> : method === "external" ? <Link2 className="h-4 w-4 mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
                {method === "in_person" ? "Download signing PDF" : method === "portal" ? "Make available in portal" : method === "external" ? "Generate link" : isResend ? "Resend" : "Send"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SAOfficeSignDialog({ open, onOpenChange, saId, onSigned }) {
  const [sig, setSig] = useState("");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [replaceNow, setReplaceNow] = useState(false);
  const sign = async () => {
    if (!sig) { toast.error("Please draw your signature."); return; }
    if (!typed.trim()) { toast.error("Please type your name."); return; }
    setBusy(true);
    try { const r = await api.saOfficeSign(saId, { signature: sig, signature_text: typed.trim(), replace_now: replaceNow }); toast.success(r?.workflow_state === "Scheduled" ? "Signed — scheduled to become active on its start date." : "Signed — agreement is now active."); onOpenChange(false); setSig(""); setTyped(""); onSigned(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sa-office-sign-dialog">
        <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><PenTool className="h-4 w-4" style={{ color: TEAL }} />Office signature</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">Counter-signing activates this agreement and writes the SA dates to the participant profile. If it starts in the future and the current agreement is still running, it is scheduled instead and takes over on its start date.</p>
        <label className="flex items-start gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={replaceNow} onChange={(e) => setReplaceNow(e.target.checked)} className="mt-0.5" data-testid="sa-replace-now" />
          Replace the current agreement immediately (e.g. funding change). The current agreement is archived now.
        </label>
        <SignaturePad onChange={setSig} />
        <div>
          <Label className="text-[11px] font-semibold text-slate-600">Your name</Label>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Type your full name" className="mt-1 h-9 text-sm" data-testid="sa-office-sign-name" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={sign} disabled={busy} style={{ backgroundColor: TEAL }} data-testid="sa-office-sign-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <PenTool className="h-4 w-4 mr-1.5" />}Sign & activate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const LEGACY_SOURCES = ["ERP Next (Formerly My Prydes Support)", "Shift Care", "Flow Logic", "Good Human"];

function SAUploadDialog({ open, onOpenChange, participant, onDone }) {
  const [source, setSource] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const [total, setTotal] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setSource(""); setStart(""); setEnd(""); setNote(""); setTotal(""); setFile(null); } }, [open]);

  const readFile = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });

  const submit = async () => {
    if (!source) { toast.error("Choose the source system."); return; }
    if (!file) { toast.error("Attach the signed PDF."); return; }
    if (file.type !== "application/pdf") { toast.error("Please attach a PDF file."); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("PDF is too large (max 15MB)."); return; }
    setBusy(true);
    try {
      const data = await readFile(file);
      await api.saUpload({ participant, source_system: source, start_date: start || null, end_date: end || null,
        note: note || null, total_value: parseFloat(total) || 0, filename: file.name, data });
      toast.success("Agreement uploaded.");
      onOpenChange(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sa-upload-dialog">
        <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><Upload className="h-4 w-4" style={{ color: TEAL }} />Upload existing agreement</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">Attach an already-signed agreement from a previous system. If its dates are the most recent it becomes the active agreement; an older one is filed as archived history.</p>
        <div className="space-y-3">
          <Fld label="Source system">
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="h-9 text-sm" data-testid="sa-upload-source"><SelectValue placeholder="Where is this from?" /></SelectTrigger>
              <SelectContent>{LEGACY_SOURCES.map((s) => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}</SelectContent>
            </Select>
          </Fld>
          <div className="grid sm:grid-cols-2 gap-3">
            <Fld label="Start date"><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 text-sm" data-testid="sa-upload-start" /></Fld>
            <Fld label="End date"><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 text-sm" data-testid="sa-upload-end" /></Fld>
          </div>
          <Fld label="Total value (optional)"><Input type="number" value={total} onChange={(e) => setTotal(e.target.value)} placeholder="0.00" className="h-9 text-sm" data-testid="sa-upload-total" /></Fld>
          <Fld label="Note (optional)"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. carried over from ShiftCare" className="h-9 text-sm" data-testid="sa-upload-note" /></Fld>
          <Fld label="Signed agreement (PDF)">
            <Input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="h-9 text-sm file:text-xs" data-testid="sa-upload-file" />
          </Fld>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} style={{ backgroundColor: TEAL }} data-testid="sa-upload-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadedSAView({ sa, canDelete, onBack }) {
  const [deleting, setDeleting] = useState(false);
  const viewPdf = async () => {
    try {
      const blob = await api.saLegacyFile(sa.id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Couldn't open the file."); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this uploaded agreement? A copy of the PDF is kept in the deletion log (Audit Log → Deleted documents). This can't be undone.")) return;
    setDeleting(true);
    try { await api.saDeleteUploaded(sa.id); toast.success("Agreement removed (snapshotted to the deletion log)."); onBack(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed."); }
    finally { setDeleting(false); }
  };
  return (
    <div data-testid="sa-uploaded-view">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800" data-testid="sa-back"><ArrowLeft className="h-3.5 w-3.5" />Back to agreements</button>
        <Badge className={`border ${STATE_TONE[sa.workflow_state] || STATE_TONE.Archived}`}>{sa.workflow_state}</Badge>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Upload className="h-4 w-4" style={{ color: TEAL }} />Uploaded agreement · {sa.source_system}
        </div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-700">
          <div><span className="text-slate-400">Participant:</span> {sa.participant_name}</div>
          <div><span className="text-slate-400">NDIS:</span> {sa.ndis_number || "—"}</div>
          <div><span className="text-slate-400">Start:</span> {fmtDate(sa.start_date)}</div>
          <div><span className="text-slate-400">End:</span> {fmtDate(sa.end_date)}</div>
          <div><span className="text-slate-400">Total value:</span> {money(sa.total_value)}</div>
        </div>
        {sa.note && <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2.5"><span className="text-slate-400 text-xs uppercase tracking-wide">Note</span><div className="mt-0.5">{sa.note}</div></div>}
        <div className="flex items-center gap-2">
          <Button size="sm" style={{ backgroundColor: TEAL }} onClick={viewPdf} data-testid="sa-uploaded-view-pdf"><FileText className="h-4 w-4 mr-1.5" />View signed PDF</Button>
          {sa.legacy_file_name && <span className="text-[11px] text-slate-400">{sa.legacy_file_name}</span>}
          {canDelete && <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 ml-auto" onClick={remove} disabled={deleting} data-testid="sa-uploaded-delete">{deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Trash2 className="h-4 w-4 mr-1.5" />}Delete</Button>}
        </div>
      </div>
      {canDelete && <p className="text-[11px] text-slate-400 mt-2">Deleting keeps a downloadable copy in Audit Log → Deleted documents. If this is the active agreement, the participant's SA dates will be cleared.</p>}
    </div>
  );
}

// Unscheduled hours pool balance display component
function UnscheduledPoolDisplay({ saId }) {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBalance = async () => {
      try {
        const data = await api.saUnscheduledBalance(saId);
        setBalance(data);
      } catch (e) {
        console.error("Failed to load unscheduled balance:", e);
      } finally {
        setLoading(false);
      }
    };
    if (saId) loadBalance();
  }, [saId]);

  if (loading) return <div className="mt-3 text-xs text-slate-400">Loading pool balance...</div>;
  if (!balance || !balance.enabled) return null;

  const percentRemaining = balance.percentage_remaining || 0;
  const isLow = percentRemaining < 20;
  const isMedium = percentRemaining >= 20 && percentRemaining < 50;
  const bgColor = isLow ? "bg-rose-50" : isMedium ? "bg-amber-50" : "bg-emerald-50";
  const textColor = isLow ? "text-rose-700" : isMedium ? "text-amber-700" : "text-emerald-700";
  const borderColor = isLow ? "border-rose-200" : isMedium ? "border-amber-200" : "border-emerald-200";

  return (
    <div className={`mt-3 rounded-lg border ${borderColor} ${bgColor} p-3`} data-testid="sa-unscheduled-balance">
      <div className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: TEAL }}>
        Unscheduled Hours Pool Status
      </div>
      <div className="grid grid-cols-3 gap-3 text-sm mb-2">
        <div>
          <div className="text-xs text-slate-500">Total Pool</div>
          <div className="font-semibold text-slate-800">{money(balance.total)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Used</div>
          <div className="font-semibold text-slate-800">{money(balance.used)}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Remaining</div>
          <div className={`font-semibold ${textColor}`}>{money(balance.remaining)}</div>
        </div>
      </div>
      <div className="relative h-2 bg-slate-200 rounded-full overflow-hidden">
        <div 
          className={`absolute top-0 left-0 h-full ${isLow ? "bg-rose-500" : isMedium ? "bg-amber-500" : "bg-emerald-500"}`}
          style={{ width: `${percentRemaining}%` }}
        />
      </div>
      <div className={`text-xs mt-1 ${textColor}`}>
        {percentRemaining.toFixed(1)}% remaining
        {isLow && " — Pool running low!"}
      </div>
      {balance.deductions && balance.deductions.length > 0 && (
        <details className="mt-3">
          <summary className="text-xs text-slate-600 cursor-pointer hover:text-slate-800">
            View {balance.deductions.length} deduction{balance.deductions.length !== 1 ? "s" : ""}
          </summary>
          <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
            {balance.deductions.map((d, i) => (
              <div key={i} className="text-xs text-slate-600 bg-white border border-slate-200 rounded px-2 py-1.5">
                <div className="flex justify-between">
                  <span className="font-mono text-[10px]">{d.shift_id}</span>
                  <span className="font-semibold">{money(d.amount)}</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  {fmtLogDate(d.deducted_at)} by {d.deducted_by}
                  {d.notes && ` · ${d.notes}`}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

